import React, { Suspense, lazy, useEffect, useState } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { useAnalytics } from '../utils/Analytics'
import { useFlag, useFlagValue } from '@labrav/flags'
import { LoadingLogo } from '@labrav/react-components'
import {
  useAuth0,
  Auth0Provider,
  CacheLocation,
  Auth0ProviderOptions,
} from '@auth0/auth0-react'
import { useDispatch } from '../../store'
import {
  auth0Domain,
  auth0ClientID,
  JWTRegex,
  labraAuth0APIAudience,
} from '../utils/constants'
import { getClaimsFromToken, getSecureKey } from '../utils/helperFunctions'
import { hydrateAuthorizationClaims } from '../modules/authorization'
import { setTelemetryAuthType } from '../../store/telemetryContext'
import { AppDispatch } from '../../store'
import {
  AUTH0_TOKEN_KEY,
  registerAuth0TokenGetter,
  unregisterAuth0TokenGetter,
} from '../api/auth'

export function applyAuth0Token(token: string, dispatch: AppDispatch): void {
  localStorage.setItem(AUTH0_TOKEN_KEY, token)
  setTelemetryAuthType('auth0')
  const claims = getClaimsFromToken(token)
  dispatch(
    hydrateAuthorizationClaims({
      source: 'auth0',
      partnerId: claims.partnerId,
      partnerUserId: claims.partnerUserId,
      roles: claims.roles,
      scopes: claims.scopes,
      isAuthenticated: true,
      claimsLoaded: true,
    })
  )
}
import {
  SalesforceContextProvider,
  useSalesforceContext,
} from '../../externalApps/SalesforceApp/contexts/SalesforceContext'
import { WithErrorBoundary } from '../components/WithErrorBoundary/WithErrorBoundary'
import { ExternalAppsAuthSourceSync } from '../components/ExternalAppsAuthSourceSync/ExternalAppsAuthSourceSync'

const ExternalAppsRoutes = lazy(() => import('../../externalApps'))
const ExternalOtpPage = lazy(() => import('../../externalApps/OtpPage'))
const LandingPageRouting = lazy(
  () => import('./components/LandingPage/LandingPageRouting')
)
const AuthWrapper = lazy(() => import('./components/AuthWrapper/AuthWrapper'))
const Auth0Register = lazy(
  () => import('./components/Auth0Redirects/Auth0Register')
)
const Auth0Logout = lazy(
  () => import('./components/Auth0Redirects/Auth0Logout')
)
const Auth0Callback = lazy(
  () => import('./components/Auth0Redirects/Auth0Callback')
)
const Auth0SAMLCallback = lazy(
  () => import('./components/Auth0Redirects/Auth0SAMLCallback')
)
const OnboardingTerms = lazy(async () => {
  const module = await import('./components/data/OnboardingTerms')
  return { default: module.OnboardingTerms }
})
const PageNotFound = lazy(async () => {
  const module = await import('../components/PageNotFound/PageNotFound')
  return { default: module.PageNotFound }
})

const SecureRoutes = () => {
  const {
    isAuthenticated,
    loginWithRedirect,
    isLoading,
    getAccessTokenSilently,
  } = useAuth0()
  const dispatch = useDispatch()
  const { loading: isFlagsLoading, value: auth0Enabled } = useFlagValue('auth0')
  const [loading, setLoading] = useState(isFlagsLoading)

  const updateAccessToken = async () => {
    try {
      const token = await getAccessTokenSilently()
      applyAuth0Token(token, dispatch)
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key) {
          if (key.includes('okta')) {
            localStorage.removeItem(key)
            i--
          }
        }
      }
      setLoading(false)
    } catch (err) {
      setLoading(false)
    }
  }

  useEffect(() => {
    registerAuth0TokenGetter(() => getAccessTokenSilently())
    return () => unregisterAuth0TokenGetter()
  }, [getAccessTokenSilently])

  useEffect(() => {
    if (auth0Enabled) {
      updateAccessToken()
    }
  }, [dispatch, isAuthenticated, auth0Enabled])

  if (loading || isLoading || isFlagsLoading) {
    return <LoadingLogo />
  }
  if (!isAuthenticated) {
    loginWithRedirect({
      authorizationParams: {
        prompt: 'login',
      },
    })
  }

  return <LandingPageRouting />
}

const SecureRoutesWrapper = () => {
  const { loading, error } = useSalesforceContext()
  const dispatch = useDispatch()

  useEffect(() => {
    const secureKey = getSecureKey()
    if (!secureKey || !new RegExp(JWTRegex).test(secureKey)) return

    try {
      const claims = getClaimsFromToken(secureKey)
      dispatch(
        hydrateAuthorizationClaims({
          source: 'url-jwt',
          partnerId: claims.partnerId,
          partnerUserId: claims.partnerUserId,
          roles: claims.roles,
          scopes:
            claims.scopes && claims.scopes.length > 0
              ? claims.scopes
              : ['all:labra:write'],
          isAuthenticated: true,
          claimsLoaded: true,
        })
      )
    } catch {
      // ignore malformed jwt; authorization slice remains unchanged
    }
  }, [dispatch])

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <LoadingLogo style={{ height: 'auto' }} />
      </div>
    )
  }

  // Salesforce context loaded successfully - skip Auth0
  if (!error) {
    return <LandingPageRouting />
  }

  // Salesforce auth failed or not in Salesforce context - use Auth0
  return <SecureRoutes />
}

export const Auth0SecuredApp = () => {
  const navigate = useNavigate()
  const onRedirectCallback = () => {
    setTimeout(() => navigate('/'), 3000)
  }

  function HandleStaleLogin() {
    const { loginWithRedirect } = useAuth0()

    useEffect(() => {
      // This restarts the flow automatically
      loginWithRedirect()
    }, [loginWithRedirect])

    return <div>Restarting session...</div>
  }

  const providerConfig: Auth0ProviderOptions = {
    domain: auth0Domain,
    clientId: auth0ClientID,
    authorizationParams: {
      audience: labraAuth0APIAudience,
      redirect_uri: window.location.origin + '/auth/callback',
    },
    onRedirectCallback: onRedirectCallback,
    cacheLocation: 'localstorage' as CacheLocation,
  }
  useAnalytics()
  const { flag: isReplaceAuthFlow, loading } = useFlag('replaceAuthFlow')
  if (loading) {
    return <></>
  }
  return (
    <Suspense
      fallback={
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <LoadingLogo style={{ height: 'auto' }} />
        </div>
      }
    >
      <Auth0Provider {...providerConfig}>
        <SalesforceContextProvider>
          <ExternalAppsAuthSourceSync />
          <WithErrorBoundary boundary="AuthSecuredApp">
            <Routes>
              <Route path="/external-apps/*" element={<ExternalAppsRoutes />} />
              <Route
                path="/aws_engine/v1/partners/:partnerId/contacts/:contactId/collab-room/invitation_id=:invitationId"
                element={<ExternalOtpPage />}
              />
              <Route path="/signin/*" element={<AuthWrapper />} />
              <Route path="/auth/register" element={<Auth0Register />} />
              <Route path="/auth/logout" element={<Auth0Logout />} />
              <Route path="/auth/callback" element={<Auth0Callback />} />
              <Route
                path="/auth/callbackSAML"
                element={<Auth0SAMLCallback />}
              />
              <Route path="/privacy" element={<OnboardingTerms />} />
              <Route path="/error" element={<HandleStaleLogin />} />
              <Route path="/auth/login" element={<HandleStaleLogin />} />
              <Route path="/*" element={<SecureRoutesWrapper />} />
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </WithErrorBoundary>
        </SalesforceContextProvider>
      </Auth0Provider>
    </Suspense>
  )
}
